import React, { useState } from "react";
import { RelatedLinks } from "../components/site/RelatedLinks";
import { Phone, MessageCircle, Mail, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "../mock";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const F = ({ label, children }) => (
  <div><label className="block text-[12px] tracking-[0.08em] uppercase font-medium text-[#3E7F8E] mb-2">{label}</label>{children}</div>
);

const inputCls = "h-12 rounded-xl border-[#183C35]/15 bg-white text-[15px] focus-visible:ring-[#183C35]/20";

const Contact = () => {
  const empty = { name: "", email: "", phone: "", subject: "", message: "" };
  const [f, setF] = useState(empty);
  const [sending, setSending] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.message) return toast.error("Please fill in your name, email and message.");
    setSending(true);
    setTimeout(() => {
      const list = JSON.parse(localStorage.getItem("aloha_messages") || "[]");
      list.unshift({ ...f, created: new Date().toISOString() });
      localStorage.setItem("aloha_messages", JSON.stringify(list));
      setSending(false);
      setF(empty);
      toast.success("Message sent! We'll get back to you shortly.");
    }, 700);
  };

  const cards = [
    [Phone, "Call us", CONTACT.phone, `tel:${CONTACT.phoneRaw}`],
    [MessageCircle, "WhatsApp", CONTACT.phone, CONTACT.whatsapp],
    [Mail, "Email us", CONTACT.email, `mailto:${CONTACT.email}`],
  ];

  return (
    <div data-testid="contact-page">
      <section className="bg-[#1A3C37] text-[#F7F3EA]">
        <div className="max-w-7xl mx-auto px-6 py-10 sm:py-20 fade-up">
          <p className="text-[13px] tracking-[0.25em] uppercase text-[#7FB8C4]">We're here 24/7</p>
          <h1 className="font-serif-d font-bold text-[34px] sm:text-[60px] mt-2 sm:mt-4">Contact us</h1>
          <p className="mt-2 sm:mt-4 text-[15px] sm:text-[18px] text-[#F7F3EA]/80">Questions, custom quotes or special requests — reach us any time.</p>
        </div>
      </section>
      <section className="py-10 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-[420px_1fr] gap-6 lg:gap-10">
          <div className="space-y-4">
            {cards.map(([I, l, v, h]) => (
              <a key={l} href={h} target={h.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={`contact-card-${l.split(" ")[0].toLowerCase()}`}
                className="lift flex items-center gap-4 bg-white rounded-2xl border border-[#183C35]/10 p-4 sm:p-5">
                <span className="w-11 h-11 rounded-lg bg-[#183C35] text-[#F7F3EA] flex items-center justify-center"><I className="w-[18px] h-[18px]" /></span>
                <span><span className="block text-[13px] text-[#202522]/60">{l}</span><span className="block text-[16px] font-medium text-[#183C35]">{v}</span></span>
              </a>
            ))}
            <div className="flex gap-6 px-1 pt-2 text-[15px] text-[#202522]/70">
              <span className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#3E7F8E]" />Corfu, Greece</span>
              <span className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#3E7F8E]" />24/7</span>
            </div>
            <iframe title="Corfu map" loading="lazy" className="w-full h-64 rounded-2xl border border-[#183C35]/10"
              src="https://www.openstreetmap.org/export/embed.html?bbox=19.70%2C39.45%2C20.10%2C39.82&layer=mapnik&marker=39.62%2C19.92" />
          </div>
          <form onSubmit={submit} className="bg-white rounded-2xl border border-[#183C35]/10 p-5 sm:p-10" data-testid="contact-form">
            <h2 className="font-serif-d text-[24px] sm:text-[32px] font-semibold text-[#183C35]">Send us a message</h2>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-5 mt-5 sm:mt-8">
              <F label="Name"><Input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" /></F>
              <F label="Email"><Input type="email" className={inputCls} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email" /></F>
              <F label="Phone"><Input className={inputCls} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="contact-phone" /></F>
              <F label="Subject"><Input className={inputCls} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="contact-subject" /></F>
            </div>
            <div className="mt-5"><F label="Message"><Textarea rows={6} className="rounded-xl border-[#183C35]/15 text-[15px]" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" /></F></div>
            <button type="submit" disabled={sending} data-testid="contact-submit"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#183C35] text-[#F7F3EA] px-7 py-3.5 font-semibold hover:bg-[#21524A] disabled:opacity-60 transition-colors duration-200">
              <Send className="w-4 h-4" />{sending ? "Sending..." : "Send message"}
            </button>
          </form>
        </div>
      </section>
      <RelatedLinks current="/contact" />
    </div>
  );
};

export default Contact;
